/**
 * Helper utilities for data manipulation
 * Framework-agnostic utility functions
 */

/**
 * Generate ID string with prefix
 * @param name Prefix string
 * @param len Current length/count
 * @returns Generated ID string
 */
export function getIdString(name: string, len: number): string {
  let returnString = "";
  let n = Math.floor(len / 10 + 1);
  let next: number = len + 1;
  returnString =
    name.substring(0, 2).toUpperCase() +
    "00000".substring(0, 5 - n) +
    next.toString();
  return returnString;
}

/**
 * Create tree structure from flat data
 * @param options Configuration options (idKey, parentKey, childrenKey)
 * @param data Flat array of data
 * @returns Tree structured data
 */
export function createTree(options: any, data: any[]): any[] {
  options = options || {};
  const ID_KEY = options.idKey || "id";
  const PARENT_KEY = options.parentKey || "parent_geo_code";
  const CHILDREN_KEY = options.childrenKey || "children";
  const tree = [];
  const childrenOf: Record<string, any[]> = {};
  let item, id, parentId;

  for (let i = 0, length = data.length; i < length; i++) {
    item = data[i];
    id = item[ID_KEY];
    parentId = item[PARENT_KEY] || 0;
    // every item may have children
    childrenOf[id] = childrenOf[id] || [];
    // init its children
    item[CHILDREN_KEY] = childrenOf[id];
    if (parentId != 0) {
      // init its parent's children object
      childrenOf[parentId] = childrenOf[parentId] || [];
      // push it into its parent's children object
      childrenOf[parentId].push(item);
    } else {
      tree.push(item);
    }
  }

  return tree;
}

/**
 * Get random integer between min and max
 * @param min Minimum value
 * @param max Maximum value
 * @returns Random integer
 */
export function getRandomInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

/**
 * Validate currency format
 * @param input Input value to validate
 * @returns True if valid currency format
 */
export function currencyValidation(input: any): boolean {
  const withDecPoint = new RegExp(/^\d+\.\d\d$/);
  const withOutDecPoint = new RegExp(/^\d+$/);
  return withDecPoint.test(input) || withOutDecPoint.test(input);
}

/**
 * Check for duplicate values in array
 * @param checkValue Value to check
 * @param checkColumn Column/property name to check
 * @param checkID ID to exclude from check
 * @param checkData Array of data to check against
 * @returns True if no duplicate found
 */
export function isDuplicate(
  checkValue: string,
  checkColumn: string,
  checkID: number,
  checkData: any[],
): boolean {
  let isValid: boolean = false;
  if (checkID > 0) {
    let found = checkData.findIndex(
      (a) => a[checkColumn] == checkValue && a.Id != checkID,
    );
    isValid = found >= 0 ? false : true;
  } else {
    let found = checkData.findIndex((a) => a[checkColumn] == checkValue);
    isValid = found >= 0 ? false : true;
  }
  return isValid;
}

/**
 * Validate that string doesn't start with whitespace
 * @param value Value to validate
 * @returns Validation result
 */
export function beginningNoSpace(value: string): {
  valid: boolean;
  error?: string;
} {
  const isWhitespace = (value || "").trim().length === 0;
  const isValid = !isWhitespace;
  return isValid ? { valid: true } : { valid: false, error: "whitespace" };
}
